import {
  Button,
  Checkbox,
  Dropdown,
  InformationBox,
  Input,
  Select,
  TextArea,
} from '@prio365/prio365-react-library';
import { Form, FormInstance, notification } from 'antd';
import moment, { Moment } from 'moment';
import React, {
  CSSProperties,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';

import { makePrioStyles } from '../../../theme/utils';

import classNames from 'classnames';
import { TFunction } from 'i18next';
import { useTheme } from 'react-jss';
import { useDispatch, useSelector } from 'react-redux';
import { ApiResult } from '../../../api';
import { getUserMe } from '../../../apps/main/rootReducer';
import CollapseWithHeader from '../../../components/CollapseWithHeader';
import CustomDateRangePicker from '../../../components/CustomDateRangePicker';
import Flex from '../../../components/Flex';
import { PrioFile } from '../../../components/Upload/UploadField';
import {
  AbsenceOverview,
  AbsenceProposal,
  AbsenceProposalFormModel,
  AbsenceProposalRequest,
  AbsentWorkDaysResult,
} from '../../../models/AbsenceProposal';
import { Contact } from '../../../models/Contact';
import { MedicalCertificate } from '../../../models/MedicalCertificate';
import {
  AbsenceState,
  AbsenceType,
  ContactId,
  DateTimeString,
  MedicalCertificateId,
  OfficeId,
} from '../../../models/Types';
import { User } from '../../../models/User';
import { PrioTheme } from '../../../theme/types';
import { debounceFunction, formatNumber, isTemporaryId } from '../../../util';
import { colon } from '../../../util/forms';
import { apiFetchPrincipals, apiFetchPrincipalsMe } from '../../employee/api';
import { fetchMonthlyCloseMe } from '../../timeKeeping/actions';
import useAccessRights from '../../users/hooks/useAccessRights';
import { deleteAbsenceProposalMe, fetchAbsencesMe } from '../actions';
import {
  apiCreateAbsenceMeProposal,
  apiFetchAbsenceProposalOverview,
  apiFetchAbsentWorkDays,
  apiFetchSubstituteOnLeave,
} from '../api';
import AbsenceProposalDetailsHolidays, {
  useOfficeHolidayDataWithDispatch,
} from './AbsenceProposalDetailsHolidays';
import MedicalCertificateDrawer, {
  MedicalCertificateDrawerRef,
} from './MedicalCertificate/MedicalCertificateDrawer';
import MedicalCertificateListItem from './MedicalCertificate/MedicalCertificateListItem';
import { useMutation } from '@tanstack/react-query';
import useContactsContext from '../../contacts/hooks/useContactsProvider';
import ContactPickerPrioCL from '../../contacts/components/ContactPickerPrioCL';
import useCompaniesContext from '../../companies/hooks/useCompaniesContext';
import AbsenceProposalFormInformationPanel from './AbsenceProposalFormInformationPanel';
import AbsenceProposalFormModal, {
  AbsenceProposalFormModalType,
} from './AbsenceProposalFormModal';
import useIndividualAbsenceNote from '../hooks/useIndividualAbsenceNote';
import { openIndividualAbsenceNoteDrawer } from '../actions/individualAbsenceNoteDrawer';

const MIN_WIDTH_FORM_ITEM = 480;

const useStyles = makePrioStyles((theme) => ({
  root: {
    width: '100%',
    maxWidth: 1024,
    display: 'flex',
    flexDirection: 'column',
    color: theme.colors.application.typography.default,
  },
  content: {
    overflow: 'hidden auto',
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  },
  absentWorkDays: {
    width: '100%',
    textAlign: 'center',
    padding: '4px',
    marginTop: '27.5px',
    fontSize: theme.font.fontSize.small,
  },
  halfDayCheckboxFirstWithAbsentWorkDays: {
    width: 'calc(45% * 0.8696 + 10px)',
    marginBottom: '15px',
    '& .prio-checkbox-input': {
      outline: 'none !important',
    },
  },
  halfDayCheckboxLastWithAbsentWorkDays: {
    marginBottom: '0px',
    '& .prio-checkbox-input': {
      outline: 'none !important',
    },
  },
  pickerDisabled: {
    backgroundColor: 'rgb(0,0,0,0.05)',
  },
  errorField: {
    '& .DateRangePickerInput__withBorder': {
      borderColor: theme.colors.base.red.default,
    },
    borderColor: theme.colors.base.red.default,
  },
  statusAlert: {
    marginTop: `${theme.spacing.small}px`,
    marginBottom: `24px`,
    '&.ant-alert.ant-alert-no-icon.ant-alert-info *': {
      color: 'black!important',
    },
  },
  statusAlertBackgroundColorGreen: {
    backgroundColor: `${theme.colors.base.green.default}80 !important`,
  },
  statusAlertBackgroundColorYellow: {
    backgroundColor: `${theme.colors.base.yellow.default}80 !important`,
  },
  statusAlertBackgroundColorRed: {
    backgroundColor: `${theme.colors.base.red.default}80 !important`,
  },
  statusAlertBackgroundColorGrey: {
    backgroundColor: `${theme.colors.base.blue[80]}80 !important`,
  },
  fontBold: {
    fontWeight: theme.font.fontWeight.bold,
  },
  dateRangePicker: {
    color: `${theme.colors.application.typography.default} !important`,
    marginBottom: theme.spacing.regular,
    '& .DateRangePickerInput__withBorder': {
      border: `1px solid ${theme.colors.application.border}`,
      height: '40px',
    },
    '& .DateInput_input__small': {
      fontSize: `${theme.font.fontSize.small}px !important`,
      color: theme.colors.application.typography.default,
    },
    '& .DateInput': {
      height: '100%',
    },
  },
  moreActionsOverlay: {
    '& .prio-dropdown-option-label': {
      fontSize: theme.font.fontSize.small,
    },
  },
}));

export type PrioFileByMedicalCertificateId = Record<
  MedicalCertificateId,
  PrioFile
>;

interface AbsenceProposalFormProps {
  className?: string;
  initialValues?: AbsenceProposalRequest | AbsenceProposal;
  editMode?: boolean;
  canSelectApplicant?: boolean;
  disableActionButton?: boolean;
  disableForm?: boolean;
  actionLabel: string;
  cancelLabel?: string;
  onFinish: (
    value: AbsenceProposalRequest | AbsenceProposal,
    medicalCertificates?: MedicalCertificate[],
    prioFilesById?: PrioFileByMedicalCertificateId
  ) => void;
  onCancel?: () => void;
  absenceOverview: AbsenceOverview | null;
  absenceState?: AbsenceState;
  isNewAbsenceProposal?: boolean;
  isUpdateAbsenceProposal?: boolean;
  setDrawerOpen?: (open: boolean) => void;
}

const fetchAbsentWorkDays = async (
  form: FormInstance<any>,
  setAbsentWorkDays: (data: AbsentWorkDaysResult) => void,
  t: TFunction,
  userMe: User,
  signal: AbortSignal,
  period?: Moment[],
  firstIsHalfDay?: boolean,
  lastIsHalfDay?: boolean,
  officeId?: OfficeId
) => {
  const from = period[0]?.format('YYYY-MM-DD');
  const to = period[1]?.format('YYYY-MM-DD');
  const internalContactId = form.getFieldValue('applicantId');
  const { result, data } = await apiFetchAbsentWorkDays(
    from,
    to,
    firstIsHalfDay,
    lastIsHalfDay,
    userMe.id !== internalContactId ? internalContactId : undefined,
    officeId
  );
  if (!signal.aborted) {
    if (result.status >= 200 && result.status < 300) {
      if (typeof data === 'object') {
        setAbsentWorkDays(data);
      }
    } else {
      notification.open({
        message: t('common:error'),
        description: t('absences:errorMessages.fetchAbsentWorkdaysError'),
      });
    }
  }
};

const debouncedFetchAbsentWorkDays = debounceFunction(fetchAbsentWorkDays, 500);

const missingPrincipalSelect = (
  principalIds: string[],
  getContactById: (id: ContactId) => Contact | undefined,
  getCompanyById: (id: string) => { shortName: string } | undefined,
  form: FormInstance<AbsenceProposalFormModel>,
  theme: PrioTheme
) => {
  principalIds = principalIds ?? [];
  const principalId = form.getFieldsValue(['principalId'])?.principalId;
  if (principalIds.includes(principalId)) {
    return null;
  }

  const principal = getContactById(principalId);

  return {
    value: principalId,
    label: (
      <Flex.Column>
        <span>{`${principal?.firstName} ${principal?.lastName}`}</span>
        <span
          style={
            {
              fontSize: theme.font.fontSize.extraSmall,
              color: theme.colors.application.typography.muted,
            } as CSSProperties
          }
        >
          {getCompanyById(principal?.companyId)?.shortName}
        </span>
      </Flex.Column>
    ),
  };
};

export const AbsenceProposalForm: React.FC<AbsenceProposalFormProps> =
  React.memo((props) => {
    const classes = useStyles();
    const theme = useTheme<PrioTheme>();
    const {
      className,
      initialValues,
      actionLabel,
      cancelLabel,
      onFinish,
      onCancel,
      editMode,
      canSelectApplicant,
      disableActionButton,
      disableForm,
      absenceOverview: absenceOverviewMe,
      absenceState,
      isNewAbsenceProposal,
      isUpdateAbsenceProposal,
      setDrawerOpen,
    } = props;

    //#region ------------------------------ Defaults
    const { t } = useTranslation();
    const [form] = Form.useForm();
    const dispatch = useDispatch();

    const absentWorkdaysController = useRef<AbortController>(null);
    //#endregion

    //#region ------------------------------ States / Attributes / Selectors
    const medicalCertificateDrawerRef =
      useRef<MedicalCertificateDrawerRef>(null);

    const [absenceOverview, setAbsenceOverview] =
      useState<AbsenceOverview>(absenceOverviewMe);

    const transformedInitialValue: AbsenceProposalFormModel = {
      ...initialValues,
      period: [
        initialValues?.from
          ? moment(initialValues.from, moment.ISO_8601)
          : moment().add('1', 'day').startOf('day'),
        initialValues?.to
          ? moment(initialValues.to, moment.ISO_8601)
          : moment().add('1', 'day').startOf('day'),
      ],
    };

    const {
      writeOtherUserAbsenceByOfficeRole,
      writeOtherUserAbsenceByGlobalRole,
    } = useAccessRights([
      'writeOtherUserAbsenceByOfficeRole',
      'writeOtherUserAbsenceByGlobalRole',
    ]);

    const userMe = useSelector(getUserMe);
    const { getContactById } = useContactsContext();
    const { getCompanyById } = useCompaniesContext();

    const [substitute, setSubstitute] = useState<ContactId>(null);
    const contactSubstitute = useMemo(() => {
      return getContactById(substitute);
    }, [getContactById, substitute]);

    const [absenceTypeOther, setAbsenceTypeOther] = useState<boolean>(
      initialValues?.absenceType === 'other'
    );

    const [absenceTypeDayOfIllness, setAbsenceTypeDayOfIllness] =
      useState<boolean>(initialValues?.absenceType === 'dayOfIllness');

    const [absenceTypeAnnualLeavePlanning, setAbsenceTypeAnnualLeavePlanning] =
      useState<boolean>(initialValues?.absenceType === 'annualLeavePlanning');

    const [absentWorkDays, setAbsentWorkDays] = useState<AbsentWorkDaysResult>({
      absentWorkdays: (initialValues as AbsenceProposal)?.absentWorkdays ?? 1,
      publicHolidays: [],
    });

    const [isSingleDay, setIsSingleDay] = useState<boolean>(
      !!initialValues && initialValues.from === initialValues.to
    );

    const [isToday, setIsToday] = useState<boolean>(true);

    const [period, setPeriod] = useState<Moment[]>([
      initialValues?.from
        ? moment(initialValues.from, moment.ISO_8601)
        : moment().add('1', 'day').startOf('day'),
      initialValues?.to
        ? moment(initialValues.to, moment.ISO_8601)
        : moment().add('1', 'day').startOf('day'),
    ]);
    const [firstIsHalfDay, setFirstIsHalfDay] = useState<boolean>(
      initialValues?.firstIsHalfDay ?? false
    );
    const [lastIsHalfDay, setLastIsHalfDay] = useState<boolean>(
      initialValues?.lastIsHalfDay ?? false
    );

    const [periodError, setPeriodError] = useState<string>(null);

    const [currentApplicantId, setCurrentApplicantId] = useState<ContactId>(
      transformedInitialValue?.applicantId ?? null
    );

    const isUserMe = currentApplicantId === userMe.id;

    const { hasAbsenceNote } = useIndividualAbsenceNote();

    const officeId = getContactById(currentApplicantId)?.officeId;

    const [principalIds, setPrincipalIds] = useState<ContactId[]>([]);
    const [additionalPrincipalIds, setAdditionalPrincipalIds] =
      useState<ContactId[]>(null);
    const [substituteOnLeave, setSubstituteOnLeave] = useState<boolean>(false);
    const [startingDaySubstituteLeave, setStartingDaySubstituteLeave] =
      useState<DateTimeString>(null);

    const [endingDaySubstituteLeave, setEndingDaySubstituteLeave] =
      useState<DateTimeString>(null);

    const [disabled, setDisabled] = useState<boolean>(false);
    const [isFormValuesChanged, setIsFormValuesChanged] =
      useState<boolean>(false);
    const [isPeriodChanged, setIsPeriodChanged] = useState<boolean>(false);
    const [isSubstituteIdEmpty, setIsSubstituteIdEmpty] =
      useState<boolean>(true);

    const isSaveChangesButtonDisabled: boolean = useMemo(() => {
      return disableActionButton
        ? disableActionButton
        : false || disableForm || !(isFormValuesChanged || isPeriodChanged);
    }, [
      disableActionButton,
      disableForm,
      isFormValuesChanged,
      isPeriodChanged,
    ]);

    const [medicalCertificates, setMedicalCertificates] = useState<
      MedicalCertificate[]
    >([]);

    const [prioFilesById, setPrioFilesById] =
      useState<PrioFileByMedicalCertificateId>({});

    const [moreActionsDropdownOpen, setMoreActionsDropdownOpen] =
      useState<boolean>(false);

    const absentOfficeHolidays = useOfficeHolidayDataWithDispatch(
      officeId,
      period
    );

    const absenceTypeSelectOptions = useMemo(
      () => [
        {
          label: t('absences:form.absenceTypes.annualLeave'),
          value: 'annualLeave',
          searchValue: t('absences:form.absenceTypes.annualLeave'),
        },
        {
          label: t('absences:form.absenceTypes.annualLeavePlanning'),
          value: 'annualLeavePlanning',
          searchValue: t('absences:form.absenceTypes.annualLeavePlanning'),
        },
        {
          label: t('absences:form.absenceTypes.overtimeCompensation'),
          value: 'overtimeCompensation',
          searchValue: t('absences:form.absenceTypes.overtimeCompensation'),
        },
        {
          label: t('absences:form.absenceTypes.paidSpecialLeave'),
          value: 'paidSpecialLeave',
          searchValue: t('absences:form.absenceTypes.paidSpecialLeave'),
        },
        {
          label: t('absences:form.absenceTypes.dayOfIllness'),
          value: 'dayOfIllness',
          searchValue: t('absences:form.absenceTypes.dayOfIllness'),
        },
        {
          label: t('absences:form.absenceTypes.school'),
          value: 'school',
          searchValue: t('absences:form.absenceTypes.school'),
        },
        {
          label: t('absences:form.absenceTypes.training'),
          value: 'training',
          searchValue: t('absences:form.absenceTypes.training'),
        },
        {
          label: t('absences:form.absenceTypes.parentalLeave'),
          value: 'parentalLeave',
          searchValue: t('absences:form.absenceTypes.parentalLeave'),
        },
        {
          label: t('absences:form.absenceTypes.maternityLeave'),
          value: 'maternityLeave',
          searchValue: t('absences:form.absenceTypes.maternityLeave'),
        },
        // {
        //   label: t('absences:form.absenceTypes.other'),
        //   value: 'other',
        // },
        // {
        //   label: t('absences:form.absenceTypes.unpaidLeave'),
        //   value: 'unpaidLeave',
        // },
      ],
      [t]
    );

    const principalSelectOptions = useMemo(() => {
      const options = principalIds.map((principalId) => {
        const principal = getContactById(principalId);
        return {
          value: principalId,
          label: (
            <Flex.Column>
              <span>{`${principal?.firstName} ${principal?.lastName}`}</span>
              <span
                style={
                  {
                    fontSize: theme.font.fontSize.extraSmall,
                    color: theme.colors.application.typography.muted,
                  } as CSSProperties
                }
              >
                {getCompanyById(principal?.companyId)?.shortName}
              </span>
            </Flex.Column>
          ),
          searchValue: `${principal?.firstName} ${principal?.lastName}`,
        };
      });

      const _options = [
        ...(missingPrincipalSelect(
          principalIds,
          getContactById,
          getCompanyById,
          form,
          theme
        )
          ? [
              missingPrincipalSelect(
                principalIds,
                getContactById,
                getCompanyById,
                form,
                theme
              ),
            ]
          : []),
        ...(options || []),
      ];

      return _options;
    }, [principalIds, getContactById, getCompanyById, form, theme]);

    const principalSelectAdditionalOptions = useMemo(() => {
      return additionalPrincipalIds?.map((id) => {
        const principal = getContactById(id);
        return {
          value: id,
          label: (
            <Flex.Column>
              <span>{`${principal?.firstName} ${principal?.lastName}`}</span>
              <span
                style={
                  {
                    fontSize: theme.font.fontSize.extraSmall,
                    color: theme.colors.application.typography.muted,
                  } as CSSProperties
                }
              >
                {getCompanyById(principal?.companyId)?.shortName}
              </span>
            </Flex.Column>
          ),
          searchValue: `${getContactById(id)?.firstName} ${getContactById(id)?.lastName}`,
        };
      });
    }, [additionalPrincipalIds, getContactById, getCompanyById, theme]);

    const [modalType, setModalType] =
      useState<AbsenceProposalFormModalType>(undefined);
    //#region ------------------------------ Methods / Handlers
    const validatePeriod = () => {
      if (period[0] && period[1]) {
        setPeriodError(null);
      } else {
        setPeriodError(t('absences:form.validation.invalidDateRange'));
      }
    };

    const handleSubstituteChange = useCallback(
      async (substitute: ContactId, from: Moment, to: Moment) => {
        if (from && to && substitute) {
          const { data } = await apiFetchSubstituteOnLeave(
            substitute,
            from.toISOString(true).split('T')[0],
            to.toISOString(true).split('T')[0]
          );
          setSubstituteOnLeave(false);
          if (data) {
            setSubstituteOnLeave(data.isAbsence);
            setStartingDaySubstituteLeave(
              moment(data.from).format('DD. MMM YYYY') ?? ''
            );
            setEndingDaySubstituteLeave(
              moment(data.to).format('DD. MMM YYYY') ?? ''
            );
          }
        }
      },
      []
    );

    const cancel = () => {
      form.resetFields();
      onCancel();
    };

    const handleFinish = (currentValue: AbsenceProposalFormModel) => {
      if (!onFinish || !period[0] || !period[1]) return;

      const { ...rest } = currentValue;
      const result: AbsenceProposalRequest = {
        ...rest,
        from: period[0].toISOString(true).split('T')[0],
        to: period[1].toISOString(true).split('T')[0],
        ...(writeOtherUserAbsenceByOfficeRole
          ? { officeId: getContactById(currentValue.applicantId)?.officeId }
          : {}),
      };
      if (editMode) {
        onFinish(result);
      } else {
        onFinish(
          result,
          medicalCertificates.filter(({ medicalCertificateId }) =>
            isTemporaryId(medicalCertificateId)
          ),
          prioFilesById
        );
      }
    };

    const filter = (contact: Contact) => {
      if (contact.contactId === userMe.id) {
        return true;
      }
      return (Object.keys(userMe?.prioData?.officeRoles) ?? []).includes(
        contact.officeId
      );
    };

    const fetchAbsenceInfo = useCallback(
      async (value: ContactId, officeId?: OfficeId) => {
        if (value !== userMe.id) {
          const fetchAbsenceOverview = () =>
            new Promise<ApiResult<AbsenceOverview>>((resolve, reject) => {
              try {
                resolve(
                  apiFetchAbsenceProposalOverview(value, undefined, officeId)
                );
              } catch (error) {
                reject(error);
              }
            });

          const { result, data } = await fetchAbsenceOverview();
          if (result.status >= 200 && result.status < 300) {
            setAbsenceOverview(data);
          } else {
            notification.open({
              message: t('common:error'),
              description: t('absences:errorMessages.fetchOverviewError'),
            });
          }
        } else {
          setAbsenceOverview(absenceOverviewMe);
        }
      },
      [userMe.id, absenceOverviewMe, t]
    );

    const getStatusAlertBackgroundColor = (
      absenceProposal: AbsenceProposal
    ) => {
      switch (absenceProposal?.absenceState) {
        case 'accepted':
        case 'revokeAccepted':
          return classes.statusAlertBackgroundColorGreen;
        case 'declined':
        case 'revokeDeclined':
          return classes.statusAlertBackgroundColorRed;
        case 'requested':
        case 'revokeRequested':
          return classes.statusAlertBackgroundColorYellow;
        case 'planned':
          return classes.statusAlertBackgroundColorGrey;
        default:
          return '';
      }
    };

    const handleOnDelete = () => {
      setModalType('delete');
    };

    const handleRequestPlannedAbsenceProposal = async () => {
      if (initialValues) {
        dispatch(
          deleteAbsenceProposalMe(
            initialValues.absenceProposalId,
            initialValues as AbsenceProposal
          )
        );
        const newAbsenceProposal: AbsenceProposalRequest = {
          ...initialValues,
          absenceType: 'annualLeave',
        };
        const { result } = await apiCreateAbsenceMeProposal(newAbsenceProposal);
        if (result.status >= 400) {
          notification.open({
            message: t('common:error'),
            description: t('documents:errorMessages.updatePermissionsError'),
          });
        }
        dispatch(fetchAbsencesMe());
        if (setDrawerOpen) {
          setDrawerOpen(false);
        }
        setMoreActionsDropdownOpen(false);
      }
    };

    const newRequestMutation = useMutation({
      mutationFn: handleRequestPlannedAbsenceProposal,
    });

    const handleOnRevoke = () => {
      setModalType('revoke');
    };

    const handleOnMedicalCertificateDrawerClose = (
      medicalCertificate: MedicalCertificate,
      file: PrioFile
    ) => {
      if (medicalCertificate) {
        setMedicalCertificates((currentState) => {
          const index = currentState.findIndex(
            (mc) =>
              mc.medicalCertificateId ===
              medicalCertificate.medicalCertificateId
          );
          if (index >= 0) {
            currentState[index] = medicalCertificate;
          } else {
            currentState.push(medicalCertificate);
          }
          return [...currentState];
        });
      }
      if (
        !editMode &&
        isTemporaryId(medicalCertificate?.medicalCertificateId) &&
        file
      ) {
        setPrioFilesById((state) => ({
          ...state,
          [medicalCertificate.medicalCertificateId]: file,
        }));
      }
    };

    const handleOnEditMedicalCertificate = (
      medicalCertificate: MedicalCertificate
    ) => {
      if (medicalCertificateDrawerRef.current) {
        medicalCertificateDrawerRef.current.setMedicalCertificate(
          medicalCertificate
        );
        medicalCertificateDrawerRef.current.setVisible(true);
      }
    };

    const handleOnArchiveMedicalCertificate = (
      medicalCertificate: MedicalCertificate
    ) => {
      setMedicalCertificates((currentState) => {
        return currentState.filter(
          (mc) =>
            mc.medicalCertificateId !== medicalCertificate.medicalCertificateId
        );
      });

      if (medicalCertificateDrawerRef.current) {
        medicalCertificateDrawerRef.current.refetchMedicalCertificates();
      }
    };
    //#endregion

    //#region ------------------------------ Effects
    useEffect(() => {
      setIsSubstituteIdEmpty(!form.getFieldValue('substituteId'));
    }, [form]);

    useEffect(() => {
      form.resetFields();
      setPrioFilesById({});
      setMedicalCertificates([]);
    }, [initialValues, form]);

    useEffect(() => {
      if (period[0] !== null && period[1] !== null) {
        setIsToday(
          period[0].format('YYYY-MM-DD') ===
            moment().add('1', 'day').startOf('day').format('YYYY-MM-DD') &&
            period[1].format('YYYY-MM-DD') ===
              moment().add('1', 'day').startOf('day').format('YYYY-MM-DD')
        );
        if (absentWorkdaysController.current) {
          absentWorkdaysController.current.abort();
        }
        absentWorkdaysController.current = new AbortController();
        const signal = absentWorkdaysController.current.signal;

        debouncedFetchAbsentWorkDays(
          form,
          setAbsentWorkDays,
          t,
          userMe,
          signal,
          period,
          firstIsHalfDay,
          lastIsHalfDay,
          officeId
        );
      }
      return () => {
        if (absentWorkdaysController.current) {
          absentWorkdaysController.current.abort();
        }
      };
    }, [
      period,
      firstIsHalfDay,
      lastIsHalfDay,
      isSingleDay,
      form,
      t,
      userMe,
      officeId,
      currentApplicantId,
    ]);

    // reset when period change form multiple to single day and halfDays are selected
    useEffect(() => {
      if (
        isSingleDay &&
        isPeriodChanged &&
        (form.getFieldValue('firstIsHalfDay') === true ||
          form.getFieldValue('lastIsHalfDay') === true)
      ) {
        form.setFieldsValue({ firstIsHalfDay: false, lastIsHalfDay: false });
        setFirstIsHalfDay(false);
        setLastIsHalfDay(false);
      }
    }, [isSingleDay, form, isPeriodChanged]);

    useEffect(() => {
      const fetchPrincipals = async () => {
        const promise = new Promise<void>(async (resolve) => {
          const { data } = canSelectApplicant
            ? await apiFetchPrincipals(currentApplicantId)
            : await apiFetchPrincipalsMe();
          if (data) {
            const principals = data.map((id) => getContactById(id));
            const principalsInMyOffice = principals
              .filter(
                (principal) =>
                  principal?.officeId ===
                  getContactById(currentApplicantId)?.officeId
              )
              .map((p) => p?.contactId);
            const otherPrincipals = principals
              .filter(
                (principal) =>
                  principal?.officeId !==
                  getContactById(currentApplicantId)?.officeId
              )
              .map((p) => p?.contactId);
            setPrincipalIds(principalsInMyOffice);

            setAdditionalPrincipalIds(
              otherPrincipals.filter(
                (id) =>
                  id !==
                  missingPrincipalSelect(
                    principalsInMyOffice,
                    getContactById,
                    getCompanyById,
                    form,
                    theme
                  )?.value
              )
            );
          }
          resolve();
        });
        return promise.then(async () => {
          if (!initialValues) {
            form.setFieldsValue({
              principalId: getContactById(currentApplicantId).managerId,
            });
          }
        });
      };
      fetchPrincipals();
    }, [
      currentApplicantId,
      canSelectApplicant,
      form,
      initialValues,
      getContactById,
      getCompanyById,
      theme,
    ]);

    useEffect(() => {
      setDisabled(disableForm || editMode);
    }, [disableForm, editMode]);

    useEffect(() => {
      if (substitute && period) {
        handleSubstituteChange(substitute, period[0], period[1]);
      }
    }, [substitute, period, handleSubstituteChange]);

    useEffect(() => {
      dispatch(fetchMonthlyCloseMe());
    }, [dispatch]);

    useEffect(() => {
      if (
        period[0]?.format('YYYY-MM-DD') !== (initialValues?.from as string) ||
        period[1]?.format('YYYY-MM-DD') !== (initialValues?.to as string)
      ) {
        setIsPeriodChanged(true);
      } else {
        setIsPeriodChanged(false);
      }
    }, [period, initialValues, isPeriodChanged]);
    //#endregion

    return (
      <>
        <Form<AbsenceProposalFormModel>
          className={classNames(classes.root, className)}
          initialValues={transformedInitialValue}
          form={form}
          onFinish={handleFinish}
          layout="vertical"
          onValuesChange={(
            changedValues: AbsenceProposalFormModel,
            allValues: AbsenceProposalFormModel
          ) => {
            if (changedValues.absenceType) {
              setAbsenceTypeOther(changedValues.absenceType === 'other');
              setAbsenceTypeDayOfIllness(
                changedValues.absenceType === 'dayOfIllness'
              );
              setAbsenceTypeAnnualLeavePlanning(
                changedValues.absenceType === 'annualLeavePlanning'
              );
            }
            if (changedValues.applicantId) {
              setCurrentApplicantId(changedValues.applicantId);
              fetchAbsenceInfo(
                changedValues.applicantId,
                writeOtherUserAbsenceByOfficeRole
                  ? getContactById(changedValues.applicantId)?.officeId
                  : undefined
              );
            }
            if (changedValues.substituteId) {
              setSubstitute(changedValues.substituteId);
            }
            if (allValues.substituteId) {
              setIsSubstituteIdEmpty(false);
            } else {
              setIsSubstituteIdEmpty(true);
            }
            if (changedValues) {
              const propsToCheck = Object.keys(changedValues);
              let changeHappened = false;
              propsToCheck.forEach((prop) => {
                if (Array.isArray(changedValues[prop])) {
                  if (
                    changedValues[prop]?.length !== initialValues[prop]?.length
                  ) {
                    changeHappened = true;
                  } else {
                    changeHappened = !(
                      initialValues[prop].every((element) =>
                        changedValues[prop].includes(element)
                      ) &&
                      changedValues[prop].every((element) =>
                        initialValues[prop].includes(element)
                      )
                    );
                  }
                } else {
                  if (changedValues?.['note'] === '') {
                    changeHappened =
                      initialValues?.['note'] === null ? false : true;
                  } else {
                    changeHappened =
                      changedValues[prop] !== initialValues[prop];
                  }
                }
              });
              setIsFormValuesChanged(changeHappened);
              if (changedValues.firstIsHalfDay === true && isSingleDay) {
                changedValues.lastIsHalfDay = false;
                form.setFieldsValue({ lastIsHalfDay: false });
                setLastIsHalfDay(false);
              }
              if (changedValues.lastIsHalfDay === true && isSingleDay) {
                changedValues.firstIsHalfDay = false;
                form.setFieldsValue({ firstIsHalfDay: false });
                setFirstIsHalfDay(false);
              }
            }
          }}
        >
          <Flex.Item flex={1} className={classes.content}>
            {absenceOverviewMe && isNewAbsenceProposal && (
              <AbsenceProposalFormInformationPanel
                employeeId={currentApplicantId}
              />
            )}
            {isUpdateAbsenceProposal && absenceState !== 'planned' && (
              <InformationBox
                className={getStatusAlertBackgroundColor(
                  initialValues as AbsenceProposal
                )}
                marginBottom="small"
                description={
                  <Flex.Row>
                    {absenceState?.includes('revoke') ? (
                      <>
                        <span>
                          {t('absences:form.statusAlertForRevoke.part1')}&nbsp;
                        </span>
                        <span className={classes.fontBold}>
                          {t('absences:form.statusAlertForRevoke.part2')}&nbsp;
                        </span>
                        <span>
                          {t('absences:form.statusAlertForRevoke.part3')}&nbsp;
                        </span>
                        <span className={classes.fontBold}>
                          {t(
                            `absences:states.${absenceState
                              .split('revoke')[1]
                              .toLowerCase()}`
                          )}
                        </span>
                        <span>{'.'}</span>
                      </>
                    ) : (
                      <>
                        <span>{t('absences:form.statusAlert')}&nbsp;</span>
                        <span className={classes.fontBold}>
                          {t(`absences:states.${absenceState}`)}
                        </span>
                        <span>{'.'}</span>
                      </>
                    )}
                  </Flex.Row>
                }
              />
            )}
            {isUserMe &&
              hasAbsenceNote &&
              isUpdateAbsenceProposal &&
              absenceState !== 'planned' && (
                <InformationBox
                  description={t('absences:individualAbsenceNote.hint')}
                  marginBottom="small"
                />
              )}
            <Flex.Row columnGap={theme.spacing.regular}>
              <Flex.Item flex={1}>
                <Form.Item
                  name="absenceType"
                  colon={colon}
                  label={t('absences:form.labels.absenceType')}
                  rules={[
                    {
                      required: true,
                    },
                  ]}
                >
                  <Select<AbsenceType>
                    disabled={disabled}
                    className={disabled ? classes.pickerDisabled : ''}
                    options={absenceTypeSelectOptions}
                  />
                </Form.Item>
              </Flex.Item>
              {absenceTypeOther && (
                <Flex.Item flex={1} minWidth={MIN_WIDTH_FORM_ITEM}>
                  <Form.Item
                    name="reason"
                    colon={colon}
                    label={t('absences:form.labels.absenceType')}
                  >
                    <Input full disabled={disabled} />
                  </Form.Item>
                </Flex.Item>
              )}
              <Flex.Item flex={1}>
                <Form.Item
                  name="applicantId"
                  label={t('absences:form.labels.applicantId')}
                  rules={[
                    {
                      required: true,
                      message: t('absences:form.validation.missingApplicantId'),
                    },
                  ]}
                >
                  <ContactPickerPrioCL
                    size="default"
                    contactType="InternalContact"
                    disabled={!canSelectApplicant || disableForm}
                    filter={
                      !writeOtherUserAbsenceByGlobalRole &&
                      writeOtherUserAbsenceByOfficeRole
                        ? filter
                        : undefined
                    }
                  />
                </Form.Item>
              </Flex.Item>
            </Flex.Row>
            <div>
              <Flex.Row childrenGap={theme.spacing.regular}>
                <Flex.Item
                  flex={20}
                  className={classNames({ [classes.errorField]: periodError })}
                >
                  <Form.Item
                    label={t('absences:form.labels.period')}
                    className={classes.dateRangePicker}
                    rules={[
                      {
                        required: true,
                      },
                    ]}
                  >
                    <CustomDateRangePicker
                      style={{
                        color: theme.colors.application.typography.default,
                        fontSize: theme.font.fontSize.small,
                      }}
                      disabled={disableForm}
                      startDate={period[0]} // momentPropTypes.momentObj or null,
                      startDateId="absenceProposal_startDateId" // PropTypes.string.isRequired,
                      startDatePlaceholderText={moment()
                        .add('1', 'day')
                        .startOf('day')
                        .format('DD.MM.YYYY')}
                      endDate={period[1]} // momentPropTypes.momentObj or null,
                      endDateId="absenceProposal_endDateId" // PropTypes.string.isRequired,
                      endDatePlaceholderText={moment()
                        .add('1', 'day')
                        .startOf('day')
                        .format('DD.MM.YYYY')}
                      onDatesChange={({ startDate, endDate }) => {
                        if (startDate === null && endDate === null) {
                          setPeriod([
                            moment().add('1', 'day').startOf('day'),
                            moment().add('1', 'day').startOf('day'),
                          ]);
                          setIsSingleDay(true);
                        } else if (startDate !== null) {
                          setPeriod([startDate, endDate]);
                          setIsSingleDay(startDate.isSame(endDate, 'day'));
                        }
                      }} // PropTypes.func.isRequired,
                      anchorDirection={'ANCHOR_RIGHT'}
                      small={true}
                      regular={false}
                      withFullScreenPortal={false}
                      daySize={30}
                      hideKeyboardShortcutsPanel={true}
                      minimumNights={0}
                      showClearDates={isToday ? false : true}
                      isOutsideRange={false}
                    />
                  </Form.Item>
                  {periodError && (
                    <div
                      style={{
                        color: theme.colors.base.red.default,
                        fontSize: theme.font.fontSize.small,
                      }}
                    >
                      {periodError ?? ' '}
                    </div>
                  )}
                </Flex.Item>
                <Flex.Item flex={3}>
                  <Input
                    full
                    value={`${formatNumber(absentWorkDays?.absentWorkdays)} ${
                      absentWorkDays?.absentWorkdays === 1
                        ? t('absences:form.labels.day')
                        : t('absences:form.labels.days')
                    }`}
                    disabled
                    className={classes.absentWorkDays}
                  />
                </Flex.Item>
              </Flex.Row>
              <Flex.Row>
                <Flex.Column flex={1}>
                  <Flex.Row>
                    <Form.Item
                      name="firstIsHalfDay"
                      colon={colon}
                      valuePropName="checked"
                      className={classes.halfDayCheckboxFirstWithAbsentWorkDays}
                    >
                      <Checkbox
                        disabled={disableForm}
                        onChange={() => setFirstIsHalfDay(!firstIsHalfDay)}
                      >
                        {t(
                          isSingleDay
                            ? 'absences:form.labels.am'
                            : 'absences:form.labels.firstIsHalfDay'
                        )}
                      </Checkbox>
                    </Form.Item>
                    <Form.Item
                      name="lastIsHalfDay"
                      colon={colon}
                      className={classes.halfDayCheckboxLastWithAbsentWorkDays}
                      valuePropName="checked"
                    >
                      <Checkbox
                        disabled={disableForm}
                        onChange={() => setLastIsHalfDay(!lastIsHalfDay)}
                      >
                        {t(
                          isSingleDay
                            ? 'absences:form.labels.pm'
                            : 'absences:form.labels.lastIsHalfDay'
                        )}
                      </Checkbox>
                    </Form.Item>
                  </Flex.Row>
                </Flex.Column>
              </Flex.Row>
            </div>
            <AbsenceProposalDetailsHolidays
              absentOfficeHolidays={absentOfficeHolidays}
            />
            <Flex.Column>
              <Flex.Row columnGap={theme.spacing.regular}>
                <Flex.Item flex={1}>
                  <Form.Item
                    name="principalId"
                    label={t('absences:form.labels.principalId')}
                    rules={[
                      {
                        required: true,
                        message: t(
                          'absences:form.validation.missingPrincipalId'
                        ),
                      },
                    ]}
                  >
                    <Select<ContactId>
                      disabled={disabled}
                      className={classNames({
                        [classes.pickerDisabled]: disabled,
                      })}
                      filterSort={(a, b) => {
                        const contactA = getContactById(a.value as string);
                        const contactB = getContactById(b.value as string);
                        return (
                          contactA?.lastName.localeCompare(
                            contactB?.lastName
                          ) ||
                          contactA?.firstName.localeCompare(contactB?.firstName)
                        );
                      }}
                      options={principalSelectOptions}
                      additionalOptions={principalSelectAdditionalOptions}
                    />
                  </Form.Item>
                </Flex.Item>
                <Flex.Item flex={1}>
                  <Form.Item
                    name="substituteId"
                    label={t('absences:form.labels.substituteId')}
                    rules={[
                      {
                        required: true,
                        message: t(
                          'absences:form.validation.missingSubstituteId'
                        ),
                      },
                    ]}
                  >
                    <ContactPickerPrioCL
                      size="default"
                      contactType="InternalContact"
                      disabled={disableForm}
                      excludedContactIds={[currentApplicantId]}
                      officeId={getContactById(currentApplicantId)?.officeId}
                    />
                  </Form.Item>
                </Flex.Item>
              </Flex.Row>
              {substituteOnLeave && contactSubstitute && absenceOverview && (
                <InformationBox
                  type={'warning'}
                  description={t(
                    `absences:form.warning.substituteOnLeave${startingDaySubstituteLeave === endingDaySubstituteLeave ? 'OneDay' : ''}`,
                    {
                      contactSubstitute: `${contactSubstitute.firstName} ${contactSubstitute.lastName}`,
                      startingDaySubstituteLeave,
                      endingDaySubstituteLeave,
                    }
                  )}
                />
              )}
            </Flex.Column>
            <Form.Item
              name="notifyContactIds"
              label={t('absences:form.labels.notifyContactIds')}
            >
              <ContactPickerPrioCL
                size="default"
                multiple
                contactType="InternalContact"
                disabled={disableForm}
                officeId={getContactById(currentApplicantId)?.officeId}
              />
            </Form.Item>
            <Form.Item
              name="note"
              label={t('absences:form.labels.notes')}
              colon={colon}
              style={{ marginBottom: 0 }}
            >
              <TextArea disabled={disableForm} rows={3} />
            </Form.Item>
            {isUserMe &&
              hasAbsenceNote &&
              !(isUpdateAbsenceProposal && absenceState !== 'planned') && (
                <InformationBox
                  description={t('absences:individualAbsenceNote.hint')}
                  marginBottom="small"
                />
              )}
            {absenceTypeDayOfIllness && medicalCertificates.length > 0 && (
              <CollapseWithHeader
                label={t('absences:form.labels.medicalCertificate', {
                  amount: medicalCertificates.length,
                })}
                enableContentBox={false}
                defaultCollapsed={false}
              >
                <Flex.Column childrenGap={theme.spacing.small}>
                  {medicalCertificates.map((medicalCertificate) => (
                    <MedicalCertificateListItem
                      medicalCertificate={medicalCertificate}
                      onEdit={handleOnEditMedicalCertificate}
                      onArchive={handleOnArchiveMedicalCertificate}
                      officeId={
                        !writeOtherUserAbsenceByGlobalRole &&
                        writeOtherUserAbsenceByOfficeRole
                          ? officeId
                          : undefined
                      }
                    />
                  ))}
                </Flex.Column>
              </CollapseWithHeader>
            )}
          </Flex.Item>
          <Flex.Row justifyContent="end" marginTop={'24px'}>
            <Flex.Row flex={1} childrenGap={theme.spacing.small}>
              {(absenceState === 'accepted' ||
                absenceState === 'revokeAccepted') && (
                <Button
                  type="danger"
                  disabled={isFormValuesChanged || isPeriodChanged}
                  onClick={handleOnRevoke}
                >
                  {t('absences:tableMenu.revoke')}
                </Button>
              )}
              {(absenceState === 'planned' || absenceState === 'requested') && (
                <Button
                  type="danger"
                  disabled={isFormValuesChanged || isPeriodChanged}
                  onClick={handleOnDelete}
                >
                  {t('absences:tableMenu.delete')}
                </Button>
              )}
              {isUserMe && !absenceTypeAnnualLeavePlanning && (
                <Button
                  type="link"
                  onClick={() => dispatch(openIndividualAbsenceNoteDrawer())}
                >
                  {hasAbsenceNote
                    ? t('absences:individualAbsenceNote.editAction')
                    : t('absences:individualAbsenceNote.addAction')}
                </Button>
              )}
              {isUserMe && absenceTypeAnnualLeavePlanning && (
                <Dropdown
                  overlayStyle={{
                    width: 'fit-content',
                  }}
                  overlayClassName={classes.moreActionsOverlay}
                  visible={moreActionsDropdownOpen}
                  onVisibleChange={(visible) =>
                    setMoreActionsDropdownOpen(visible)
                  }
                  options={[
                    {
                      label: t('absences:tableMenu.request'),
                      disabled: isFormValuesChanged || isPeriodChanged,
                      loading: newRequestMutation.isLoading,
                      onClick: handleRequestPlannedAbsenceProposal,
                      value: 'request',
                    },
                    {
                      label: hasAbsenceNote
                        ? t('absences:individualAbsenceNote.editAction')
                        : t('absences:individualAbsenceNote.addAction'),
                      value: 'absenceNote',
                      onClick: () => {
                        dispatch(openIndividualAbsenceNoteDrawer());
                        setMoreActionsDropdownOpen(false);
                      },
                    },
                  ]}
                >
                  <Button type="default">
                    {t('absences:actions.moreActions')}
                  </Button>
                </Dropdown>
              )}

              {isUserMe && absenceTypeDayOfIllness && (
                <Button
                  onClick={() => {
                    medicalCertificateDrawerRef.current?.setVisible(true);
                  }}
                  type="default"
                  iconProp={['fal', 'file-medical']}
                >
                  {t('absences:medicalCertificate.addMedicalCertificate')}
                </Button>
              )}
            </Flex.Row>
            {onCancel && (
              <Flex.Item>
                <Button
                  type="default"
                  onClick={cancel}
                  style={{ marginRight: '8px' }}
                >
                  {cancelLabel}
                </Button>
              </Flex.Item>
            )}

            {!!actionLabel && (
              <Flex.Item>
                <Button
                  htmlType="submit"
                  disabled={isSaveChangesButtonDisabled || isSubstituteIdEmpty}
                  onClick={validatePeriod}
                >
                  {actionLabel}
                </Button>
              </Flex.Item>
            )}
          </Flex.Row>
          {absenceTypeDayOfIllness && (
            <MedicalCertificateDrawer
              ref={medicalCertificateDrawerRef}
              from={period[0] && period[0].toISOString(true).split('T')[0]}
              to={period[1] && period[1].toISOString(true).split('T')[0]}
              absenceProposalId={
                (initialValues as AbsenceProposal)?.absenceProposalId
              }
              onMedicalCertificatedFetch={setMedicalCertificates}
              officeId={
                !writeOtherUserAbsenceByGlobalRole &&
                writeOtherUserAbsenceByOfficeRole
                  ? officeId
                  : undefined
              }
              isUserMe={initialValues.applicantId === userMe.id}
              onClose={handleOnMedicalCertificateDrawerClose}
              isEditMode={!!editMode}
            />
          )}
        </Form>
        <AbsenceProposalFormModal
          type={modalType}
          absenceProposal={initialValues as AbsenceProposal}
          setDrawerOpen={setDrawerOpen}
          visible={modalType !== undefined}
          setModalType={setModalType}
        />
      </>
    );
  });

export default AbsenceProposalForm;
